import React, { useEffect, useState } from 'react';
import { Box, MenuItem, TextField, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import { FormControlStyled, FormLabelStyled } from '../common/Dialog/DialogGlynt';
import { DetectionPattern, PatternMode } from '../../types/vendor-types';

export interface DetectionPatternFormData {
  label?: string | null;
  pattern: string;
  pattern_mode: PatternMode;
  case_sensitive: boolean;
  priority: number;
}

interface DetectionPatternDialogProps {
  open: boolean;
  loading: boolean;
  pattern: DetectionPattern | null;
  vendorName: string;
  onClose: () => void;
  onSave: (data: DetectionPatternFormData) => void;
}

const defaultForm: DetectionPatternFormData = {
  label: '',
  pattern: '',
  pattern_mode: 'KEYWORD',
  case_sensitive: false,
  priority: 0,
};

const DetectionPatternDialog: React.FC<DetectionPatternDialogProps> = ({
  open,
  loading,
  pattern,
  vendorName,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<DetectionPatternFormData>(defaultForm);

  useEffect(() => {
    if (open) {
      if (pattern) {
        setForm({
          label: pattern.label || '',
          pattern: pattern.pattern,
          pattern_mode: pattern.pattern_mode || 'KEYWORD',
          case_sensitive: !!pattern.case_sensitive,
          priority: pattern.priority ?? 0,
        });
      } else {
        setForm(defaultForm);
      }
    }
  }, [open, pattern]);

  const handleChange = <K extends keyof DetectionPatternFormData>(
    key: K,
    value: DetectionPatternFormData[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!form.pattern.trim()) return;
    onSave({
      label: form.label?.trim() ? form.label.trim() : undefined,
      pattern: form.pattern.trim(),
      pattern_mode: form.pattern_mode,
      case_sensitive: form.case_sensitive,
      priority: form.priority,
    });
  };

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      cypressId="dialog-detection-pattern"
      loading={loading}
      width="600px"
    >
      <DialogTitleGlynt
        title={pattern ? `Edit Detection Pattern for ${vendorName}` : `Add Detection Pattern for ${vendorName}`}
        closeModal={onClose}
        loading={loading}
      />
      {/* Wrap content and actions in a form so Enter submits */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!loading) handleSubmit();
        }}
      >
        <DialogContentGlynt>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Row 1: Label + Mode */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlStyled sx={{ flex: 1, minWidth: 200 }}>
                <FormLabelStyled>LABEL</FormLabelStyled>
                <TextField
                  value={form.label}
                  onChange={(e) => handleChange('label', e.target.value)}
                  size="small"
                  variant="outlined"
                  fullWidth
                  placeholder="Optional label"
                  data-cy="detection-pattern-label"
                />
              </FormControlStyled>
              <FormControlStyled sx={{ flex: 1, minWidth: 160 }}>
                <FormLabelStyled>MODE</FormLabelStyled>
                <TextField
                  select
                  value={form.pattern_mode}
                  onChange={(e) => handleChange('pattern_mode', e.target.value as PatternMode)}
                  size="small"
                  variant="outlined"
                  fullWidth
                  data-cy="detection-pattern-mode-select"
                >
                  <MenuItem value="KEYWORD">KEYWORD</MenuItem>
                  <MenuItem value="REGEX">REGEX</MenuItem>
                </TextField>
              </FormControlStyled>
            </Box>
            {/* Row 2: Pattern */}
            <FormControlStyled fullWidth>
              <FormLabelStyled>PATTERN *</FormLabelStyled>
              <TextField
                required
                value={form.pattern}
                onChange={(e) => handleChange('pattern', e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
                data-cy="detection-pattern-input"
                placeholder="Enter pattern"
              />
            </FormControlStyled>
            {/* Row 3: Case Sensitive + Priority */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlStyled sx={{ flex: 1, minWidth: 160 }}>
                <FormLabelStyled>CASE SENSITIVE</FormLabelStyled>
                <TextField
                  select
                  value={form.case_sensitive ? 'yes' : 'no'}
                  onChange={(e) => handleChange('case_sensitive', e.target.value === 'yes')}
                  size="small"
                  variant="outlined"
                  fullWidth
                  data-cy="detection-pattern-case-sensitive"
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
              </FormControlStyled>
              <FormControlStyled sx={{ flex: 1, minWidth: 160 }}>
                <FormLabelStyled sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  PRIORITY
                  <Tooltip
                    title="Processing priority - lower numbers checked first"
                    enterDelay={300}
                    placement="top"
                    arrow
                  >
                    <HelpOutlineIcon
                      fontSize="inherit"
                      sx={{ color: 'text.secondary', opacity: 0.7 }}
                      data-cy="detection-pattern-priority-help"
                    />
                  </Tooltip>
                </FormLabelStyled>
                <TextField
                  type="number"
                  value={form.priority}
                  onChange={(e) => handleChange('priority', Number(e.target.value))}
                  size="small"
                  variant="outlined"
                  fullWidth
                  inputProps={{ min: 0 }}
                  data-cy="detection-pattern-priority"
                />
              </FormControlStyled>
            </Box>
          </Box>
        </DialogContentGlynt>
        {/* Hidden submit so Enter works consistently across browsers */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
        <DialogActionsGlynt
          handleSubmit={handleSubmit}
          closeModal={onClose}
          cancelCypressId="detection-pattern-cancel"
          submitCypressId="detection-pattern-save"
          clearCypressId=""
          submitButtonText={pattern ? 'Save' : 'Add'}
          submitLoading={loading}
          submitDisabled={!form.pattern.trim() || loading}
          showClearButton={false}
          showCancelButton={true}
          showSubmitButton={true}
        />
      </form>
    </DialogGlynt>
  );
};

export default DetectionPatternDialog;
